import { Badge, Tooltip } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useQueryClient } from '@tanstack/react-query';
import { IconCloudOff, IconRefresh } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { flush, subscribe } from './queue';

/** Zeigt Offline-Status und ausstehende Änderungen; synchronisiert bei Verbindung (PWA-03). */
export function OfflineIndicator() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));
  const [pending, setPending] = useState(0);

  useEffect(() => subscribe(setPending), []);

  useEffect(() => {
    const sync = async () => {
      const problems = await flush();
      if (problems.length > 0) {
        notifications.show({
          color: 'orange',
          title: t('offline.conflictTitle', { count: problems.length }),
          message: problems.map((p) => p.detail).join(' · '),
          autoClose: false,
        });
      }
      void qc.invalidateQueries();
    };
    const goOnline = () => {
      setOnline(true);
      void sync();
    };
    const goOffline = () => setOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    if (navigator.onLine) void flush();
    const interval = window.setInterval(() => navigator.onLine && void flush(), 30_000);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
      window.clearInterval(interval);
    };
  }, [qc, t]);

  if (online && pending === 0) return null;
  return (
    <Tooltip label={online ? t('offline.syncing') : t('offline.hint')}>
      <Badge
        color={online ? 'blue' : 'gray'}
        variant="light"
        leftSection={online ? <IconRefresh size={12} /> : <IconCloudOff size={12} />}
        style={{ cursor: 'default' }}
      >
        {online ? t('offline.pending', { count: pending }) : pending > 0 ? t('offline.offlinePending', { count: pending }) : t('offline.offline')}
      </Badge>
    </Tooltip>
  );
}
